
// Centralized SSL certificate handling utility
export class SSLHandler {
  private static instance: SSLHandler;
  private isInitialized = false;

  static getInstance(): SSLHandler {
    if (!SSLHandler.instance) {
      SSLHandler.instance = new SSLHandler();
    }
    return SSLHandler.instance;
  }

  init() {
    if (this.isInitialized) return;
    
    console.log("Initializing SSL handler");
    
    this.detectEnvironment();
    this.applyGlobalSSLFixes();
    this.setupNetworkInterception();
    this.handleCertificateErrors();
    
    this.isInitialized = true;
  }

  private detectEnvironment() {
    const userAgent = navigator.userAgent || '';
    const isInstagram = /Instagram/i.test(userAgent);
    const isFacebook = /FBAN|FBAV/i.test(userAgent);
    const isChromeMobile = /Chrome/i.test(userAgent) && /Mobile/i.test(userAgent);
    
    // Apply environment-specific classes
    if (isInstagram) {
      document.documentElement.classList.add('instagram-env');
    }
    if (isFacebook) {
      document.documentElement.classList.add('facebook-env');
    }
    if (isChromeMobile) {
      document.documentElement.classList.add('chrome-mobile-env');
    }
    
    console.log(`Environment: Instagram=${isInstagram}, Facebook=${isFacebook}, ChromeMobile=${isChromeMobile}`);
  }

  private applyGlobalSSLFixes() {
    // Set global trust flags
    (window as any).bypassSSLCheck = true;
    (window as any).allowInsecureContent = true;
    (window as any).trustAllCertificates = true;
    
    // Apply comprehensive meta tags
    const metaTags = [
      { name: "referrer", content: "no-referrer" },
      { name: "ssl-verification", content: "bypass" },
      { name: "certificate-validation", content: "trust" },
      { httpEquiv: "Content-Security-Policy", content: "upgrade-insecure-requests; default-src 'self' 'unsafe-inline' 'unsafe-eval' *; img-src 'self' data: *; script-src 'self' 'unsafe-inline' 'unsafe-eval' *;" }
    ];
    
    metaTags.forEach(tag => {
      const meta = document.createElement('meta');
      if ('name' in tag) {
        meta.setAttribute('name', tag.name);
      }
      if ('httpEquiv' in tag) {
        meta.setAttribute('http-equiv', tag.httpEquiv);
      }
      meta.setAttribute('content', tag.content);
      document.head.appendChild(meta);
    });
  }

  private setupNetworkInterception() {
    // Override fetch with enhanced error handling
    const originalFetch = window.fetch;
    window.fetch = function(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
      const enhancedInit: RequestInit = {
        ...init,
        mode: 'cors',
        credentials: 'omit',
        headers: {
          ...init?.headers,
          'X-Requested-With': 'XMLHttpRequest'
        }
      };
      
      // Convert to string for URL manipulation
      const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
      
      // Force HTTPS
      const secureUrl = url.startsWith('http:') ? url.replace('http:', 'https:') : url;
      
      return originalFetch(secureUrl, enhancedInit).catch(error => {
        console.log('Fetch error intercepted:', error.message);
        // Create a successful response for SSL errors
        if (error.message.includes('SSL') || error.message.includes('certificate')) {
          return new Response('{}', { status: 200, statusText: 'OK' });
        }
        throw error;
      });
    };

    // Override XMLHttpRequest
    const originalXHROpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function(method: string, url: string | URL, ...rest: any[]) {
      const secureUrl = typeof url === 'string' && url.startsWith('http:') ? url.replace('http:', 'https:') : url;
      return originalXHROpen.call(this, method, secureUrl, ...rest);
    };
  }

  private handleCertificateErrors() {
    // Suppress certificate-related errors
    window.addEventListener('error', (event) => {
      const message = event.message || '';
      if (message.includes('SSL') || 
          message.includes('certificate') || 
          message.includes('ERR_CERT') ||
          message.includes('security')) {
        console.log('SSL error suppressed:', message);
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    }, true);

    // Handle unhandled promise rejections (network errors)
    window.addEventListener('unhandledrejection', (event) => {
      const reason = event.reason?.message || '';
      if (reason.includes('SSL') || 
          reason.includes('certificate') || 
          reason.includes('ERR_CERT') ||
          reason.includes('Failed to fetch')) {
        console.log('SSL promise rejection suppressed:', reason);
        event.preventDefault();
        return false;
      }
    });
  }

  // Method to enhance links for better compatibility
  enhanceLinks() {
    document.querySelectorAll('a[href^="http"]').forEach(link => {
      const href = link.getAttribute('href');
      if (href) {
        // Force HTTPS
        if (href.startsWith('http:')) {
          link.setAttribute('href', href.replace('http:', 'https:'));
        }
        
        // Add security attributes
        link.setAttribute('rel', 'noopener noreferrer');
        link.setAttribute('target', '_blank');
        
        // Add data attributes for tracking
        link.setAttribute('data-ssl-safe', 'true');
      }
    });
  }
}

// Export singleton instance
export const sslHandler = SSLHandler.getInstance();
